import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, InputBase, Tooltip } from '@material-ui/core';
import { FileCopy, Replay, Stars } from '@material-ui/icons';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { FEAT_POINTS, FeatRanks } from '@pvplevelup/core/dist/feats/FeatTypes';
import { decodeSpec, describeSpec, encodeSpec, getFeatClass, getSpecClass, requiredLevel, spentPoints } from '@pvplevelup/core/dist/feats/FeatSpec';
import { PAGE_KIT, PageHeader, usePageStyles } from 'components/Page/PageKit';
import { FeatTrees } from 'components/Feats/FeatTrees';
import { useFeatClasses } from 'components/Feats/useFeatClasses';
import { classImages } from 'utilities/ClassImages';

const useStyles = makeStyles((theme) => ({
  classes: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
    gridGap: 6,
    maxWidth: 12 * 50,
    padding: theme.spacing(1.25, 0),
  },
  classButton: {
    width: 44,
    height: 44,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 10,
    cursor: 'pointer',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    background: 'rgba(0, 0, 0, 0.25)',
    transition: 'border-color 0.15s, background 0.15s',
    '& img': { width: 30, height: 30, opacity: 0.55, transition: 'opacity 0.15s' },
    '&:hover img': { opacity: 1 },
  },
  classSelected: {
    borderColor: PAGE_KIT.accent,
    background: 'rgba(66, 165, 245, 0.16)',
    boxShadow: `0 0 0 3px rgba(66, 165, 245, 0.15)`,
    '& img': { opacity: 1 },
  },
  bar: {
    display: 'grid',
    gridTemplateColumns: 'auto auto minmax(0, 1fr) auto',
    gridColumnGap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderRadius: 12,
    background: PAGE_KIT.panel,
    border: PAGE_KIT.border,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'auto auto',
      gridRowGap: theme.spacing(1.25),
    },
  },
  stat: {
    display: 'grid',
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
  },
  statValue: {
    fontSize: '1.2rem',
    fontWeight: 800,
  },
  full: {
    color: '#ffd54f',
  },
  spec: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    gridColumnGap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
    },
  },
  specInput: {
    height: PAGE_KIT.controlHeight,
    padding: '0 12px',
    borderRadius: 10,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&.Mui-focused': {
      borderColor: PAGE_KIT.accent,
    },
  },
  specError: {
    borderColor: '#e57373',
  },
  button: {
    height: PAGE_KIT.controlHeight,
    textTransform: 'none',
    fontWeight: 700,
  },
  centered: {
    display: 'grid',
    placeItems: 'center',
    minHeight: 300,
  },
}));

const CLASSES = Object.values(AocClasses) as AocClasses[];

/**
 * Plan a feat build, or look at one: pick a class and spend the 79 points, or paste a spec code (the string the game
 * reports, also shown on character and game pages). The URL keeps the build (/feats?spec=...), so it can be shared.
 */
export const FeatPlanner: React.FC = () => {
  const classes = useStyles();
  const page = usePageStyles();
  const history = useHistory();
  const location = useLocation();
  const featClasses = useFeatClasses();
  const params = new URLSearchParams(location.search);
  const urlSpec = params.get('spec') || '';
  const urlClass = (getSpecClass(urlSpec) || params.get('class') || AocClasses.Barbarian) as AocClasses;

  const [aocClass, setAocClass] = useState<AocClasses>(urlClass);
  const [ranks, setRanks] = useState<FeatRanks>({});
  const [text, setText] = useState(urlSpec);
  const [copied, setCopied] = useState(false);
  const data = featClasses && getFeatClass(featClasses, aocClass);

  // a spec in the URL (opened from a character or game) loads once the trees are there
  useEffect(() => {
    if(!featClasses || !urlSpec) return;
    const specData = getFeatClass(featClasses, getSpecClass(urlSpec));
    if(specData){
      setAocClass(specData.class);
      setRanks(decodeSpec(specData, urlSpec).ranks);
      setText(urlSpec);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featClasses]);

  const spec = useMemo(() => data ? encodeSpec(data, ranks) : '', [data, ranks]);
  // keep the URL on the build so it can be shared, and the box on the build's code
  useEffect(() => {
    if(!data) return;
    const search = spentPoints(ranks) > 0 ? `?spec=${spec}` : `?class=${encodeURIComponent(aocClass)}`;
    if(location.search != search) history.replace({pathname: location.pathname, search});
    setText(spentPoints(ranks) > 0 ? spec : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, aocClass, data]);

  const typedClass = featClasses && getFeatClass(featClasses, getSpecClass(text.trim()));
  const textValid = text.trim() == '' || (typedClass != null && decodeSpec(typedClass, text.trim()).unknown.length == 0);
  const loadText = (value: string) => {
    setText(value);
    const trimmed = value.trim();
    const target = featClasses && getFeatClass(featClasses, getSpecClass(trimmed));
    if(!target) return;
    const decoded = decodeSpec(target, trimmed);
    if(decoded.unknown.length == 0){
      setAocClass(target.class);
      setRanks(decoded.ranks);
    }
  };
  const pickClass = (c: AocClasses) => {
    if(c == aocClass) return;
    setAocClass(c);
    setRanks({});
  };
  const copy = () => {
    navigator.clipboard.writeText(spec).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };

  const spent = spentPoints(ranks);
  return (
    <div className="content-container">
      <PageHeader icon={<Stars />} title="Feat Planner"
        subtitle={data && spent > 0 ? `${aocClass}: ${describeSpec(data, ranks)}` : 'Pick a class and spend your points, or paste a spec code from a character or game.'}
        childrenStyle="filters">
        <div className={classes.classes}>
          {CLASSES.map((c) => (
            <Tooltip key={c} title={c}>
              <div className={clsx(classes.classButton, {[classes.classSelected]: c == aocClass})} onClick={() => pickClass(c)}>
                <img src={classImages.getColorIcon(c)} alt={c} />
              </div>
            </Tooltip>
          ))}
        </div>
      </PageHeader>
      {!data ? <div className={classes.centered}><CircularProgress /></div> : (
        <React.Fragment>
          <div className={classes.bar}>
            <div className={classes.stat}>
              <span className={classes.statLabel}>Points</span>
              <span className={clsx(classes.statValue, {[classes.full]: spent >= FEAT_POINTS})}>{spent} / {FEAT_POINTS}</span>
            </div>
            <div className={classes.stat}>
              <span className={classes.statLabel}>Level</span>
              <span className={classes.statValue}>{requiredLevel(data, ranks) || '-'}</span>
            </div>
            <div className={classes.spec}>
              <InputBase className={clsx(classes.specInput, {[classes.specError]: !textValid})} placeholder="Paste a spec code" value={text}
                onChange={(e) => loadText(e.target.value)} inputProps={{spellCheck: false, 'aria-label': 'Spec code'}} />
              <Tooltip title={copied ? 'Copied' : 'Copy the spec code'}>
                <span>
                  <Button className={classes.button} variant="outlined" startIcon={<FileCopy />} disabled={spent == 0} onClick={copy}>Copy</Button>
                </span>
              </Tooltip>
            </div>
            <Button className={classes.button} variant="outlined" startIcon={<Replay />} disabled={spent == 0} onClick={() => setRanks({})}>Reset</Button>
          </div>
          {!textValid && <div className={page.muted} style={{marginTop: -8, marginBottom: 12}}>That isn't a spec code the planner recognises.</div>}
          <FeatTrees data={data} ranks={ranks} onChange={setRanks} />
        </React.Fragment>
      )}
    </div>
  );
};
